import React, {
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import GroupsEditDialog from "./GroupsEditDialog";
import { ImportContext } from "~/Context/ImportContext";
import { useTranslation } from "react-i18next";

const GroupsEditWrapper = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    tracksIndex,
    trackGroups,
    updateGroupName,
    ungroupTracks,
    ungroupTrack,
  } = useContext(ImportContext);
  const [isOpen, setIsOpen] = useState(true);

  const groups = useMemo(() => {
    if (!tracksIndex || !trackGroups) return [];

    return trackGroups.map((ctxGroup) => {
      const relatedTracks = tracksIndex.filter(
        (t) => t["info.groupId"] === ctxGroup.id,
      );
      return {
        ...ctxGroup,
        count: relatedTracks.length,
        tracks: relatedTracks.map((t) => ({
          id: t.id,
          name: t["info.name"] || t("track.unnamedTrack"),
        })),
      };
    });
  }, [tracksIndex, trackGroups, t]);

  const handleRenameGroup = useCallback(
    (id, name) => {
      updateGroupName(id, name);
    },
    [updateGroupName],
  );

  const handleDelete = useCallback(
    (groupId) => {
      ungroupTracks(groupId);
      // No need to close the dialog yet, `groups` recalculation acts reactively.
    },
    [ungroupTracks],
  );

  const handleRemoveTrack = useCallback(
    (trackId) => {
      ungroupTrack(trackId);
    },
    [ungroupTrack],
  );

  const handleAfterAnimate = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  useEffect(() => {
    if (groups.length === 0) {
      navigate(-1, { replace: true });
    }
  }, [groups.length, navigate]);

  if (groups.length === 0) {
    return null;
  }

  return (
    <GroupsEditDialog
      open={isOpen}
      groups={groups}
      onAfterAnimate={handleAfterAnimate}
      onClose={handleClose}
      onRenameGroup={handleRenameGroup}
      onDelete={handleDelete}
      onRemoveTrack={handleRemoveTrack}
      title={t("import.groupsEditTitle")}
    />
  );
};

export default GroupsEditWrapper;
